import { type CSSProperties, type ReactNode } from 'react';

import { labelSpan } from './BarLabelOverlay.sty.js';
import { BAR_LABEL_MIN_SIZE, BAR_LABEL_PADDING } from './label-utils.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth12pixelsSize400Map } from '../../../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../../../core/components/text-ellipsis/truncate-string.js';

interface VerticalBarLabelProps {
  x: number;
  y: number;
  barWidth: number;
  barHeight: number;
  canvasHeight: number;
  isReversed: boolean;
  text: string;
  insideColor: string;
  outsideColor: string;
  truncationMode: TruncationMode;
}

/**
 * HTML label for a single vertical (rotated) bar — see `HorizontalBarLabel`
 * for the coloring approach explanation.
 *
 * When `isReversed` is false (BTT, normal) the bar grows upward and the label
 * is rotated −90°, reading bottom-to-top.
 *
 * When `isReversed` is true (the numerical Y axis has `reversed` set, i.e.
 * TTB) the bar grows downward from the chart top and the label is rotated
 * +90°, reading top-to-bottom.
 */
export function VerticalBarLabel({
  x,
  y,
  barWidth,
  barHeight,
  canvasHeight,
  isReversed,
  text,
  insideColor,
  outsideColor,
  truncationMode,
}: Readonly<VerticalBarLabelProps>): ReactNode {
  // For vertical bars the rotated text's *height* (font line-box) sits across
  // the bar's WIDTH. The bar height does not constrain a rotated label.
  if (barWidth < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const availLength = canvasHeight - 2 * BAR_LABEL_PADDING;
  if (availLength < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const truncated = truncateString(
    text,
    availLength,
    charsFontWidth12pixelsSize400Map,
    truncationMode,
  );

  const centerX = x + barWidth / 2;
  const centerY = canvasHeight / 2;

  const rotation = isReversed ? 'rotate(90deg)' : 'rotate(-90deg)';

  const toGradientPosition = (screenY: number) => {
    const t = (screenY - BAR_LABEL_PADDING) / availLength;
    return isReversed ? t : 1 - t;
  };

  const barStart = toGradientPosition(y);
  const barEnd = toGradientPosition(y + barHeight);
  const insideStart = Math.max(0, Math.min(1, Math.min(barStart, barEnd)));
  const insideEnd = Math.max(0, Math.min(1, Math.max(barStart, barEnd)));
  const insideStartPercent = insideStart * 100;
  const insideEndPercent = insideEnd * 100;

  const gradient = `linear-gradient(to right, ${outsideColor} 0%, ${outsideColor} ${insideStartPercent}%, ${insideColor} ${insideStartPercent}%, ${insideColor} ${insideEndPercent}%, ${outsideColor} ${insideEndPercent}%, ${outsideColor} 100%)`;

  const style: CSSProperties = {
    left: centerX,
    top: centerY,
    transform: `translate(-50%, -50%) ${rotation}`,
    width: availLength,
    backgroundImage: gradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  };

  return (
    <span className={labelSpan} style={style}>
      {truncated}
    </span>
  );
}

VerticalBarLabel['displayName'] = 'VerticalBarLabel';
